/**
 * Gestos sobre el visor.
 *
 * Un dedo enfoca donde se toca. Dos dedos hacen zoom. Un toque doble alterna
 * entre 1× y 2×.
 *
 * El toque simple se resuelve al instante, sin esperar a ver si llega un
 * segundo: retrasar el enfoque 250 ms para poder distinguirlo se nota mucho en
 * la mano. Un toque doble enfoca *y además* cambia el zoom, que es inofensivo.
 *
 * No depende de la cámara: se prueba con eventos sintéticos.
 */

import { useCallback, useRef } from 'react';
import type React from 'react';

/** Movimiento máximo (px) para que un toque siga contando como toque. */
const TAP_SLOP_PX = 12;
/** Duración máxima (ms) de un toque. */
const TAP_MAX_MS = 350;
/** Separación máxima (ms) entre los dos toques de un toque doble. */
const DOUBLE_TAP_MS = 320;

export interface PinchZoomHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
}

interface Options {
  /** Factor actual, para arrancar el pellizco desde donde está. */
  factor: number;
  onZoom: (factor: number) => void;
  onTap: (clientX: number, clientY: number) => void;
  onDoubleTap: () => void;
  disabled?: boolean;
}

const distance = (
  a: { x: number; y: number },
  b: { x: number; y: number }
): number => Math.hypot(a.x - b.x, a.y - b.y);

export const usePinchZoom = ({
  factor,
  onZoom,
  onTap,
  onDoubleTap,
  disabled = false,
}: Options): PinchZoomHandlers => {
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ startDist: number; startFactor: number } | null>(null);
  const tap = useRef<{ x: number; y: number; at: number; moved: boolean } | null>(
    null
  );
  const lastTapAt = useRef(0);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return;
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointers.current.size === 2) {
        const [a, b] = [...pointers.current.values()];
        pinch.current = { startDist: distance(a, b), startFactor: factor };
        // El segundo dedo cancela el toque: esto es un pellizco, no un enfoque.
        tap.current = null;
        return;
      }

      if (pointers.current.size === 1) {
        tap.current = { x: e.clientX, y: e.clientY, at: Date.now(), moved: false };
      }
    },
    [disabled, factor]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return;
      if (!pointers.current.has(e.pointerId)) return;
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (tap.current) {
        const moved =
          Math.abs(e.clientX - tap.current.x) > TAP_SLOP_PX ||
          Math.abs(e.clientY - tap.current.y) > TAP_SLOP_PX;
        if (moved) tap.current.moved = true;
      }

      if (pointers.current.size === 2 && pinch.current) {
        const [a, b] = [...pointers.current.values()];
        const dist = distance(a, b);
        if (pinch.current.startDist > 0) {
          onZoom((pinch.current.startFactor * dist) / pinch.current.startDist);
        }
      }
    },
    [disabled, onZoom]
  );

  const finishPointer = useCallback(
    (e: React.PointerEvent, cancelled: boolean) => {
      if (disabled) return;
      const wasPinching = pointers.current.size === 2;
      pointers.current.delete(e.pointerId);
      if (pointers.current.size < 2) pinch.current = null;

      if (cancelled || wasPinching) {
        tap.current = null;
        return;
      }

      const t = tap.current;
      tap.current = null;
      if (!t || t.moved) return;
      if (Date.now() - t.at > TAP_MAX_MS) return;

      const now = Date.now();
      const isDouble = now - lastTapAt.current <= DOUBLE_TAP_MS;
      lastTapAt.current = isDouble ? 0 : now;

      onTap(t.x, t.y);
      if (isDouble) onDoubleTap();
    },
    [disabled, onTap, onDoubleTap]
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => finishPointer(e, false),
    [finishPointer]
  );

  const onPointerCancel = useCallback(
    (e: React.PointerEvent) => finishPointer(e, true),
    [finishPointer]
  );

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
};
